"use client"

import { useCallback, useRef, useState } from "react"
import type { NodeViewProps } from "@tiptap/react"
import { NodeViewWrapper } from "@tiptap/react"
import { FileIcon, UploadCloudIcon } from "lucide-react"
import "src/tiptap/components/tiptap-node/image-upload-node/image-upload-node.css"
import { focusNextNode, isValidPosition } from "src/tiptap/lib/tiptap-utils"
import {
  createImageUploadAdapter,
  formatImageUploadSize,
  type UploadedImageAttrs,
  validateImageFiles,
} from "src/tiptap/components/tiptap-node/image-upload-node/image-upload-kit"

const getUploadLimitText = ({
  limit,
  maxSize,
}: {
  limit: number
  maxSize: number
}) => {
  const hasLimit = limit > 0
  const hasMaxSize = maxSize > 0
  const fileLabel = `${limit} file${limit === 1 ? "" : "s"}`

  if (hasLimit && hasMaxSize) {
    return `Maximum ${fileLabel}, ${formatImageUploadSize(maxSize)} each.`
  }

  if (hasLimit) {
    return `Maximum ${fileLabel}.`
  }

  if (hasMaxSize) {
    return `Maximum ${formatImageUploadSize(maxSize)} each.`
  }

  return "Image files only."
}

export const ImageUploadNodeView = (props: NodeViewProps) => {
  const { accept, limit, maxSize } = props.node.attrs
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const extensionOptions = props.extension.options

  const replaceWithImages = useCallback(
    (imageAttrs: UploadedImageAttrs[]) => {
      const pos = props.getPos()

      if (!isValidPosition(pos)) {
        return false
      }

      const currentNode = props.editor.state.doc.nodeAt(pos)
      if (currentNode?.type.name !== props.node.type.name) {
        return false
      }

      const inserted = props.editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + props.node.nodeSize })
        .insertContentAt(
          pos,
          imageAttrs.map((attrs) => ({
            type: extensionOptions.type,
            attrs,
          }))
        )
        .run()

      if (inserted) {
        imageAttrs.forEach((attrs) => extensionOptions.onSuccess?.(attrs))
        focusNextNode(props.editor)
      }

      return inserted
    },
    [extensionOptions, props]
  )

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!extensionOptions.uploadImage) {
        extensionOptions.onError?.(new Error("Upload function is not defined."))
        return
      }

      setIsUploading(true)

      try {
        validateImageFiles(files, { maxSize, limit })
        const upload = createImageUploadAdapter({
          uploadImage: extensionOptions.uploadImage,
          maxSize,
        })
        const imageAttrs = await Promise.all(files.map((file) => upload(file)))
        replaceWithImages(imageAttrs)
      } catch (error) {
        extensionOptions.onError?.(
          error instanceof Error ? error : new Error("Image upload failed")
        )
      } finally {
        setIsUploading(false)
      }
    },
    [extensionOptions, limit, maxSize, replaceWithImages]
  )

  const chooseFiles = useCallback(() => {
    if (isUploading) return
    inputRef.current?.click()
  }, [isUploading])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return

      event.preventDefault()
      chooseFiles()
    },
    [chooseFiles]
  )

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? [])
      event.target.value = ""

      if (files.length) {
        void uploadFiles(files)
      }
    },
    [uploadFiles]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragging(false)

      const files = Array.from(event.dataTransfer.files)
      if (files.length) {
        void uploadFiles(files)
      }
    },
    [uploadFiles]
  )

  const uploadLimitText = getUploadLimitText({ limit, maxSize })

  return (
    <NodeViewWrapper
      className={`tiptap-image-upload ${isDragging ? "is-dragging" : ""}`}
      data-uploading={isUploading ? "true" : undefined}
      role="button"
      tabIndex={isUploading ? -1 : 0}
      aria-label="Upload image"
      aria-busy={isUploading || undefined}
      onClick={chooseFiles}
      onDragEnter={(event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={(event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsDragging(false)
        }
      }}
      onDragOver={(event: React.DragEvent<HTMLDivElement>) =>
        event.preventDefault()
      }
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
    >
      <div className="tiptap-image-upload-visual" aria-hidden="true">
        <FileIcon className="tiptap-image-upload-file-icon" />
        <span className="tiptap-image-upload-badge">
          <UploadCloudIcon className="tiptap-image-upload-badge-icon" />
        </span>
      </div>
      <div className="tiptap-image-upload-copy">
        <div className="tiptap-image-upload-title">
          {isUploading ? (
            "Uploading image..."
          ) : (
            <>
              <span className="tiptap-image-upload-action">
                Click to upload
              </span>
              <span> or drag and drop</span>
            </>
          )}
        </div>
        <div className="tiptap-image-upload-hint">{uploadLimitText}</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={limit > 1}
        hidden
        onClick={(event) => event.stopPropagation()}
        onChange={handleFileChange}
      />
    </NodeViewWrapper>
  )
}
